/**
 * Phase 4.5 — Call Orchestrator WS connector
 *
 * Opens its OWN WebSocket to /ws/sales/alerts/ (separate from
 * SalesAlertToaster) and routes messages of type `sales.call_event` to
 * the callOrchestratorStore. Other types are ignored.
 *
 * The connection is gated by the per-domain feature flag — if it's OFF,
 * we never even open the socket.
 *
 * Reload-safe: on first connect we also call /panel/orchestrator/current/
 * so the dock restores active call state after F5.
 */
import { onMounted, onBeforeUnmount, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { getAccessToken } from '@/auth/tokenManager'
import { isNative } from '@/native/platform'
import { useCallOrchestratorStore } from '@/stores/callOrchestratorStore'
import { useEditingGuardRegistry } from '@/stores/editingGuardRegistry'

let _ws = null
let _reconnectTimer = null
let _reconnectAttempts = 0
let _pingTimer = null
let _hookCount = 0  // refcount to avoid duplicate sockets when multiple components call this

// Keep below CHANNEL_LAYERS group_expiry (60s); server also re-joins every ~25s.
const PING_INTERVAL_MS = 25000

function _wsUrl() {
  const loc = window.location
  const proto = loc.protocol === 'https:' ? 'wss:' : 'ws:'
  // Domain is derived server-side from the signed token (tenant isolation); the
  // client no longer supplies domain_id (which could subscribe to another tenant).
  const token = getAccessToken() || ''
  return `${proto}//${loc.host}/ws/sales/alerts/?token=${encodeURIComponent(token)}`
}

function _clearPing() {
  if (_pingTimer) { clearInterval(_pingTimer); _pingTimer = null }
}

export function useCallOrchestratorWS() {
  const authStore = useAuthStore()
  const orch = useCallOrchestratorStore()
  const guard = useEditingGuardRegistry()

  function connect() {
    if (!authStore.isAuthenticated) return
    if (!orch.enabled) {
      orch.setWsStatus('disabled')
      return
    }
    if (_ws && (_ws.readyState === WebSocket.OPEN || _ws.readyState === WebSocket.CONNECTING)) return

    orch.setWsStatus('connecting')
    const url = _wsUrl(authStore.domain?.id || authStore.user?.primary_domain_id || 1)
    try {
      _ws = new WebSocket(url)
    } catch (e) {
      console.warn('[orchWS] ctor failed:', e)
      _scheduleReconnect()
      return
    }

    _ws.onopen = () => {
      _reconnectAttempts = 0
      orch.setWsStatus('connected')
      console.log('[orchWS] connected')
      // Reload-safe restore: pick up any in-progress call we missed
      orch.loadCurrentCall().catch(() => {})
      _clearPing()
      _pingTimer = setInterval(() => {
        try {
          if (_ws && _ws.readyState === WebSocket.OPEN) {
            _ws.send(JSON.stringify({ type: 'ping' }))
          }
        } catch (_) { /* ignore */ }
      }, PING_INTERVAL_MS)
    }

    _ws.onmessage = (evt) => {
      let msg
      try { msg = JSON.parse(evt.data) } catch { return }
      if (msg?.type === 'pong' || msg?.type === 'connected') return
      if (msg?.type !== 'sales.call_event') return  // SalesAlertToaster handles its own

      // Phase 4.6 — Hybrid B+C targeting filter.
      // Backend sets `recipient_user_ids` based on:
      //   - target_operators_json (personal call → only those user_ids)
      //   - active sales operators (broadcast → all online sales.view users)
      //   - null  (open mode — outbound or no info → everyone in the domain)
      const recipients = msg?.recipient_user_ids
      if (Array.isArray(recipients) && recipients.length > 0) {
        const myId = authStore.user?.id
        if (!myId || !recipients.includes(Number(myId))) {
          // Not for me — drop silently. Logged at debug to keep console clean.
          if (window?.__orch_debug) {
            console.debug('[orchWS] ignored event (not in recipients)', {
              myId, recipients, mode: msg?.target_mode, subtype: msg?.subtype,
            })
          }
          return
        }
      }

      const dirty = guard.getDirtyForms()
      orch.handleEvent(msg, dirty)
    }

    _ws.onclose = (evt) => {
      console.warn('[orchWS] closed', evt?.code)
      _clearPing()
      _ws = null
      if (orch.enabled) {
        orch.setWsStatus('reconnecting')
        _scheduleReconnect()
      } else {
        orch.setWsStatus('disabled')
      }
    }

    _ws.onerror = (e) => {
      console.warn('[orchWS] error', e?.message || e)
    }
  }

  function _scheduleReconnect() {
    if (_reconnectTimer) return
    _reconnectAttempts = Math.min(_reconnectAttempts + 1, 6)
    const delay = Math.min(30000, 1000 * Math.pow(2, _reconnectAttempts))
    _reconnectTimer = setTimeout(() => {
      _reconnectTimer = null
      connect()
    }, delay)
  }

  function disconnect() {
    if (_reconnectTimer) { clearTimeout(_reconnectTimer); _reconnectTimer = null }
    _clearPing()
    try { _ws?.close() } catch {}
    _ws = null
    orch.setWsStatus('disabled')
  }

  // First init → load flag + user prefs + maybe connect. Loaded in parallel
  // because they are independent (flag drives visibility of the bell/banner
  // at all; prefs drive the muted UX). Guarded by flagLoaded/userPrefsLoaded
  // so a late re-run (login after boot on /login) never duplicates requests.
  async function _init() {
    // Technician native app has no sales orchestrator; skip entirely.
    if (isNative()) return
    try {
      if (window.location.pathname.startsWith('/login')) return
    } catch (_) { /* ignore */ }
    const tasks = []
    if (!orch.flagLoaded) tasks.push(orch.loadFlag())
    if (!orch.userPrefsLoaded) tasks.push(orch.loadUserPrefs())
    await Promise.all(tasks).catch(() => {})
    if (orch.enabled) connect()
  }

  onMounted(async () => {
    _hookCount++
    // Do NOT fire on anonymous boot (login page): the calls 401 → refresh 400
    // → logout redirect, feeding a reload loop on /login.
    if (_hookCount === 1 && authStore.isAuthenticated) {
      await _init()
    }
  })

  // Login happens via SPA navigation (no page reload), so init after auth.
  watch(() => authStore.isAuthenticated, (now, prev) => {
    if (now && !prev) _init()
  })

  onBeforeUnmount(() => {
    _hookCount = Math.max(0, _hookCount - 1)
    if (_hookCount === 0) disconnect()
  })

  // React to flag toggling at runtime (admin enables the feature)
  watch(() => orch.enabled, (now, prev) => {
    if (now && !prev) connect()
    if (!now && prev) disconnect()
  })

  return { connect, disconnect }
}
